<template>
  <div class="gf_loading show">
    <div class="gf_loading_section">
      <div class="gfLoader-frame gf_loading-frame d-flex flex-column align-items-center">
        <LogoLoading />
        <div class="gf_loading_section-progress">
          <div class="gf_loading_section-progress-loading" :style="{ width: loadingCount + '%' }"></div>
        </div>
      </div>
      <div class="gf_loading_section-label">{{ desc }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import LogoLoading from '@/views/GLoader/LogoLoading.vue';
import { isInIframe } from '@/app/utils/helpers';
import { app } from '@/store';

export default defineComponent({
  name: 'LoadingContentEditor',
  components: {
    LogoLoading,
  },
  data: () => ({
    dataMessages: [
      {
        title: 'If the editor is frozen, please contact our support via live chat',
      },
    ],
    loadingCount: 0,
    currentStep: 1,
    title: '',
    tips: [
      {
        desc: 'You can change Row padding by hovering on and dragging the edge of the Row',
      },
      {
        desc: 'Turn on dynamic button for product module to prevent another dynamic product module automatically inserted into the product page.',
      },
      {
        desc: "After finishing design, it's better to save your templates into library to reuse anytime or use it for backup ",
      },
      {
        desc: 'If the Quantity element is lack of + and -, you can quickly fix this by replacing it with a new Quantity element.',
      },
      {
        desc: 'If you want to show a feature or section you desire, you can custom code by putting HTML Code into Liquid element.',
      },
      {
        desc: 'Text Block includes the live editor which allows you to edit the font style right in the editing screen',
      },
      {
        desc: 'Notice: Do not put a Product Module into another Product Module',
      },
      {
        desc: 'You can upload background image in Hero Banner for a specific device',
      },
      {
        desc: 'Use (P)Swatches to display each variant as an alternative for (P)Variant',
      },
      {
        desc: 'It is highly recommended to design for Desktop to small screen sizes to avoid messing up',
      },
      {
        desc: 'Related Product will show products in the same collections with the original product.',
      },
      {
        desc: 'Product List on live page will show products in order arranged in Shopify Admin',
      },
      {
        desc: 'With Contact Form, collected info will be sent to Customer Email Address in Shopify Admin',
      },
      {
        desc: 'Amazing tip: Use Button to trigger a Popup When button is clicked.',
      },
      {
        desc: 'Need help or information? Do not hesitate to visit out our Knowledge Base and master page building with GemPages.',
      },
      {
        desc: "Don't forget to hit Save before exiting from your design",
      },
    ],
    desc: '',
  }),
  computed: {
    ...mapState('page', {
      loadingFrameMessage: 'loadingFrameMessage',
    }),
  },
  created() {
    this.$center.gloader = this;
    this.title = this.dataMessages[0].title;
    this.desc = this.tips[0].desc;
    this.showLoading();
  },
  mounted() {
    window.addEventListener('message', this.listenMessage);
  },
  unmounted() {
    window.removeEventListener('message', this.listenMessage);
  },
  methods: {
    showLoading() {
      const randomNumber = this.getRandomNumber(0, this.tips.length - 1);
      const tip = this.tips[randomNumber] || this.tips[0];
      this.desc = tip.desc;

      let dataMessages = this.dataMessages;
      if (this.loadingFrameMessage) {
        dataMessages = [
          {
            title: this.loadingFrameMessage,
          },
        ];
      }
      if (dataMessages != undefined) {
        const time = 1500;
        for (let i = 0; i < dataMessages.length; i++) {
          const timer = i * time;
          this.setStatus(i, timer);
        }
      }
      return false;
    },
    getRandomNumber(min: number, max: number) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    setStatus(i: number, timer: number) {
      setTimeout(() => {
        const message = this.dataMessages[i];
        this.currentStep = i + 1;
        this.title = message.title;
        this.setLoadingProgressBar(1, 97, timer + 1500);
      }, timer);
    },
    setLoadingProgressBar(start = 1, end = 97, duration = 1500) {
      let startTimestamp: any = null;
      const step = (timestamp: any) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const count = Math.floor(progress * (end - start) + start);
        this.loadingCount = isNaN(count) ? 0 : count;
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    },
    listenMessage(event: MessageEvent) {
      if (!isInIframe()) return;
      const gemSession = app.$store.getters['app/gemSession'] || '';

      try {
        const { data } = event;
        if (data.type === 'trigger-save-editor') {
          let url = window.location.href;
          const urlParams = new URLSearchParams(window.location.search);
          if (!urlParams.has('token')) {
            url += '&token=' + gemSession;
          }
          (window as any).top.location.href = url;
        }
      } catch {
        //
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';

.gf_loading {
  position: absolute;
  top: 0;
  left: 0;
  background: map-get($hybrid-shade-light, '300');
  z-index: 9999999;
  transition: display 0.5s;
  @include transition(opacity 0.3s ease-in-out);

  width: 0px;
  height: 0px;
  overflow: hidden;
  @include opacity(0);

  &.show {
    @include opacity(1);
    display: block !important;
    width: 100%;
    height: 100%;
  }

  &-frame {
    display: flex;
    justify-content: center;

    svg {
      display: inline;
      vertical-align: unset;
    }

    &-logo {
      margin-right: 16px;

      svg {
        path {
          &:nth-of-type(1) {
            animation: blink1 2s infinite;
          }

          &:nth-of-type(2) {
            animation: blink2 2s infinite;
          }

          &:nth-of-type(3) {
            animation: blink3 2s infinite;
          }
        }
      }
    }
  }

  &_section {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .gf_loading-frame-logo {
      margin-right: 0;
    }

    &-progress {
      width: 184px;
      height: 4px;
      background: map-get($hybrid-shade-light, '500');
      border-radius: 2px;
      margin-top: 24px;

      &-loading {
        background: map-get($primary-blue, '275');
        border-radius: 2px;
        height: 4px;
      }
    }

    &-label {
      position: absolute;
      bottom: 68px;
      left: 50%;
      transform: translateX(-50%);
      color: map-get($text-light-mode, 'disabled');
      text-align: center;
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
    }
  }
}

@keyframes blink1 {
  0% {
    opacity: 0.25;
  }

  25% {
    opacity: 1;
  }

  75% {
    opacity: 1;
  }

  100% {
    opacity: 0.25;
  }
}

@keyframes blink2 {
  0% {
    opacity: 0.25;
  }

  25% {
    opacity: 0.25;
  }

  50% {
    opacity: 1;
  }

  75% {
    opacity: 1;
  }

  100% {
    opacity: 0.25;
  }
}

@keyframes blink3 {
  0% {
    opacity: 0.25;
  }

  25% {
    opacity: 0.25;
  }

  50% {
    opacity: 0.25;
  }

  75% {
    opacity: 1;
  }

  100% {
    opacity: 0.25;
  }
}
</style>
